import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import { Activity, ArrowLeft } from "lucide-react";
import DeviceLimitModal from "../components/DeviceLimitModal.jsx";
import api from "../lib/api";
import analytics from "../lib/analytics";

export default function Login() {
  const { login, verifyDeviceOtp, resendDeviceOtp } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [deviceCapDevices, setDeviceCapDevices] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  // New-device OTP step
  const [otpStep, setOtpStep] = useState(null); // { device_label, ttl_minutes }
  const [otpCode, setOtpCode] = useState("");
  const [resending, setResending] = useState(false);
  const reason = new URLSearchParams(window.location.search).get("reason");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.otp_required) {
        setOtpStep({ device_label: result.device_label, ttl_minutes: result.ttl_minutes });
        toast.message("We emailed you a verification code for this new device.");
        return;
      }
      toast.success("Welcome back");
      nav("/app/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.code === "device_limit_reached") {
        setDeviceCapDevices(detail.devices || []);
        analytics.error("login_failed", "device_limit_reached", "Login");
      } else {
        toast.error(detail || "Login failed");
        analytics.error("login_failed", (typeof detail === "string" ? detail : err.response?.status) || "unknown", "Login");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyDeviceOtp(form.email, form.password, otpCode.trim());
      toast.success("Device verified — welcome back");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setResending(true);
    try {
      await resendDeviceOtp(form.email, form.password);
      toast.success("A new code is on its way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend the code");
    } finally {
      setResending(false);
    }
  };

  const cancelOtp = () => {
    setOtpStep(null);
    setOtpCode("");
  };

  // Revoke a chosen device and retry login. Uses an unauthenticated
  // password-bound revoke endpoint because the new device has no JWT yet.
  const revokeAndRetry = async (deviceId) => {
    setRevokingId(deviceId);
    try {
      await api.post("/auth/devices/revoke-with-password", {
        email: form.email,
        password: form.password,
        revoke_device_id: deviceId,
      });
      toast.success("Device removed — sending a verification code...");
      setDeviceCapDevices(null);
      const result = await login(form.email, form.password);
      if (result?.otp_required) {
        setOtpStep({ device_label: result.device_label, ttl_minutes: result.ttl_minutes });
        return;
      }
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove that device");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid md:grid-cols-2">
      <div className="hidden md:flex relative overflow-hidden bg-[#0B5FFF] text-white items-end p-12">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B5FFF] via-[#1E3A8A] to-[#0A54E0]" />
        <div className="absolute inset-0 medical-grid opacity-20" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-md bg-white text-[#111827] flex items-center justify-center">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-extrabold">RapidCare SA</div>
              <div className="text-[10px] tracking-widest text-slate-300 uppercase">Clinical Reference</div>
            </div>
          </div>
          <h2 className="font-heading text-3xl font-bold max-w-md leading-tight">
            Evidence-based answers at the bedside — in under 10 seconds.
          </h2>
          <p className="mt-4 text-white/80 max-w-md text-sm">
            Protocols, drug dosing, interaction checks and calculators — all tuned for South African clinical practice.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="font-heading text-3xl font-bold text-[#111827]">Welcome back</h1>
          <p className="text-slate-600 mt-2 text-sm">Sign in to your RapidCare SA account.</p>
          {reason === "other_device" && (
            <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="session-notice">
              You were signed out because this account was used to sign in on another device. Accounts may only be used on one device at a time.
            </div>
          )}
          <Card className="mt-8 border-slate-200">
            <CardContent className="p-6">
              {otpStep ? (
                <div data-testid="otp-step">
                  <h2 className="font-heading text-lg font-semibold text-[#111827]">Verify this device</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    For your security, we emailed a 6-digit code to{" "}
                    <span className="font-medium text-slate-800">{form.email}</span>. Enter it to authorise{" "}
                    <span className="font-medium">{otpStep.device_label || "this device"}</span>.
                  </p>
                  <div className="mt-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900">
                    RapidCare accounts are licensed to a single registered clinician and limited to a set number of devices. If you didn't try to sign in, change your password.
                  </div>
                  <form onSubmit={verifyOtp} className="space-y-4 mt-5">
                    <div>
                      <Label htmlFor="otp">Verification code</Label>
                      <Input
                        id="otp"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        required
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="######"
                        className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono"
                        data-testid="otp-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || otpCode.length < 6}
                      className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11"
                      data-testid="otp-verify-btn"
                    >
                      {loading ? "Verifying..." : "Verify & sign in"}
                    </Button>
                  </form>
                  <div className="flex items-center justify-between mt-4 text-sm">
                    <button
                      type="button"
                      onClick={resendOtp}
                      disabled={resending}
                      className="text-[#111827] hover:underline disabled:opacity-50"
                      data-testid="otp-resend-btn"
                    >
                      {resending ? "Sending..." : "Resend code"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelOtp}
                      className="text-slate-500 hover:underline"
                      data-testid="otp-cancel-btn"
                    >
                      Use a different account
                    </button>
                  </div>
                </div>
              ) : (
              <>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@clinic.co.za"
                    className="mt-1.5"
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-[#111827] hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1.5"
                    data-testid="login-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0B5FFF] hover:bg-[#0A54E0] h-11"
                  data-testid="login-submit"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
              <p className="text-sm text-slate-600 text-center mt-5">
                No account?{" "}
                <Link to="/signup" className="text-[#111827] font-medium hover:underline" data-testid="go-signup">
                  Start free trial
                </Link>
              </p>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <DeviceLimitModal
        open={!!deviceCapDevices}
        onClose={() => setDeviceCapDevices(null)}
        devices={deviceCapDevices || []}
        loadingId={revokingId}
        onRevoke={revokeAndRetry}
      />
    </div>
  );
}
